/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: Mega Menu Home Runtime Gate
   Project: La Troupe
   Version: 1.0.0
   Status: LLCL-QA
   ----------------------------------------------------------
   Home defers the full desktop mega-menu controller until
   real header intent. Non-Home routes and Shopify Theme Editor
   preserve immediate controller behavior through the frozen
   v6.4 baseline asset.
   ========================================================== */
(() => {
  'use strict';

  const HEADER = '.ll-header--llcl';
  const TRIGGER = '[data-ll-menu-trigger]';
  const BLOCK = '[data-ll-mega-block]';
  const MENU_KEY = '[data-ll-menu-key]';
  const GLOBAL_KEY = '__LLCL_MEGA_MENU_V64__';
  const desktop = window.matchMedia('(min-width: 700px)');
  const currentScript = document.currentScript;
  const currentSrc = currentScript?.src || '';
  const coreSrc = currentSrc.replace(
    /ll-mega-menu\.js(?=([?#]|$))/,
    'll-mega-menu-c10r-baseline.js'
  );

  let loadPromise = null;
  let armed = false;

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const cleanup = () => {
    if (!armed) return;
    armed = false;
    document.removeEventListener('pointerover', onIntent, true);
    document.removeEventListener('pointerdown', onIntent, true);
    document.removeEventListener('focusin', onIntent, true);
  };

  const findBlock = (header, trigger) => {
    const key = normalize(trigger?.dataset?.llMenuTrigger);
    if (!key) return null;

    return Array.from(header.querySelectorAll(BLOCK)).find((block) => {
      const keyNode = block.matches(MENU_KEY) ? block : block.querySelector(MENU_KEY);
      const blockKey = normalize(block.dataset.llMenuKey || keyNode?.dataset?.llMenuKey);
      return blockKey === key;
    }) || null;
  };

  const loadCore = () => {
    if (window[GLOBAL_KEY]) return Promise.resolve(window[GLOBAL_KEY]);
    if (loadPromise) return loadPromise;
    if (!coreSrc || coreSrc === currentSrc) return Promise.resolve(null);

    cleanup();

    loadPromise = new Promise((resolve) => {
      const existing = document.querySelector('script[data-ll-mega-core-runtime]');

      if (existing) {
        if (window[GLOBAL_KEY]) {
          resolve(window[GLOBAL_KEY]);
          return;
        }

        existing.addEventListener('load', () => resolve(window[GLOBAL_KEY] || null), { once: true });
        existing.addEventListener('error', () => resolve(null), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = coreSrc;
      script.async = false;
      script.dataset.llMegaCoreRuntime = '1';
      script.addEventListener('load', () => resolve(window[GLOBAL_KEY] || null), { once: true });
      script.addEventListener('error', () => {
        loadPromise = null;
        resolve(null);
      }, { once: true });
      document.head.appendChild(script);
    });

    return loadPromise;
  };

  function replayIntent(event, target) {
    const trigger = target?.closest?.(TRIGGER);
    const header = trigger?.closest?.(HEADER);
    if (!trigger || !header) return;

    const eventType = event.type;
    const pointerType = event.pointerType || '';

    loadCore().then((api) => {
      if (!api?.openBlock || !desktop.matches || !trigger.isConnected) return;
      if (eventType === 'pointerover' && pointerType === 'touch') return;
      if (eventType === 'pointerover' && !trigger.matches(':hover')) return;
      if (eventType === 'focusin' && document.activeElement !== trigger) return;

      const block = findBlock(header, trigger);
      if (!block || block.classList.contains('is-open')) return;
      api.openBlock(header, trigger, block);
    });
  }

  function onIntent(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest(HEADER)) return;

    const trigger = target.closest(TRIGGER);
    const request = loadCore();

    if (trigger) {
      request.then(() => replayIntent(event, target));
    }
  }

  const arm = () => {
    if (armed || !desktop.matches) return;
    armed = true;
    document.addEventListener('pointerover', onIntent, true);
    document.addEventListener('pointerdown', onIntent, true);
    document.addEventListener('focusin', onIntent, true);
  };

  const isHome = document.body?.classList.contains('ll-home-route');

  if (!isHome || window.Shopify?.designMode) {
    loadCore();
    return;
  }

  if (typeof desktop.addEventListener === 'function') {
    desktop.addEventListener('change', (event) => {
      if (event.matches) arm();
      else cleanup();
    });
  }

  arm();
})();
